import React, { useState, useCallback } from 'react';
import './App.css';

const STEP_LABELS = ['Business', 'Financial', 'Market', 'Operations', 'Team', 'Docs', 'Legal', 'Contact'];

const App = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState({
    financial: {},
    operational: {},
    legal: {},
    market: {},
    team: {},
    documentation: {},
    business_info: {},
    contact_info: {}
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailValidation, setEmailValidation] = useState({ status: 'idle', message: '', suggestion: null });
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  const sections = [
    {
      id: 'business_info',
      title: 'Business Information',
      description: 'Tell us about your business',
      questions: [
        { id: 'company_name', text: 'Company Name', type: 'text', required: true },
        { id: 'industry', text: 'Industry Sector', type: 'text', required: true },
        { id: 'annual_revenue', text: 'Annual Revenue (\u00A3)', type: 'select', required: true, options: [
          '\u00A31-5 million', '\u00A35-10 million', '\u00A310-20 million', '\u00A320-50 million', '\u00A350+ million'
        ]},
        { id: 'employee_count', text: 'Number of Employees', type: 'select', required: true, options: [
          '1-10', '10-25', '25-50', '50-100', '100+'
        ]}
      ]
    },
    {
      id: 'financial',
      title: 'Financial Clarity',
      description: 'Buyers will only trust what they can see in the numbers \u2014 how clear and reliable are yours?',
      questions: [
        { id: 'revenue_growth', text: 'Revenue growth over the past 3 years', type: 'scale', 
          labels: ['Declining significantly', 'Declining slightly', 'Stable', 'Growing steadily', 'Growing rapidly'] },
        { id: 'profitability', text: 'Current profitability and margins', type: 'scale',
          labels: ['Loss-making', 'Break-even', 'Low margins', 'Good margins', 'Excellent margins'] },
        { id: 'cash_flow', text: 'Do you generate predictable cash flow', type: 'scale',
          labels: ['Highly unpredictable', 'Somewhat unpredictable', 'Generally stable', 'Very stable', 'Highly predictable'] },
        { id: 'financial_reporting', text: 'Financial reporting and audit', type: 'scale',
          labels: ['Annual accounts only', 'Quarterly, no audit', 'Monthly, no audit', 'Monthly + annual audit', 'Audit-ready on demand'] },
        { id: 'debt_structure', text: 'Company debt and access to credit', type: 'scale',
          labels: ['High debt, no credit', 'Some debt, limited credit', 'Low debt, good credit', 'No debt, easy credit', 'No debt, excess cash'] }
      ]
    },
    {
      id: 'market',
      title: 'Customer Base Strength & Market Position',
      description: 'Is your revenue safe and well-spread? Is there a believable upside for a buyer?',
      questions: [
        { id: 'competitive_advantage', text: 'Sustainable competitive advantages', type: 'scale',
          labels: ['No clear advantage', 'Weak advantage', 'Some advantages', 'Strong advantages', 'Dominant position'] },
        { id: 'customer_concentration', text: 'Customer spread', type: 'scale',
          labels: ['Reliant on 1-2 clients', 'Too few customers', 'Some concentration risk', 'Good spread, low risk', 'Well diversified'] },
        { id: 'market_trends', text: 'Growth story & market opportunity', type: 'scale',
          labels: ['Shrinking market', 'Stagnant, minimal growth', 'Stable market', 'Growing, data-backed', 'Strong growth outlook'] },
        { id: 'brand_reputation', text: 'Brand strength & market reputation', type: 'scale',
          labels: ['Unknown, no repeat trade', 'High churn, limited profile', 'Some repeat business', 'Strong loyalty, good reputation', 'Recognised, defensible brand'] }
      ]
    },
    {
      id: 'operational',
      title: 'How the Business Runs',
      description: 'Efficient systems and less reliance on you means smoother operations and more value',
      questions: [
        { id: 'process_documentation', text: 'Business processes are documented and systematized', type: 'scale',
          labels: ['Not documented', 'Partially documented', 'Mostly documented', 'Well documented', 'Fully systematized'] },
        { id: 'technology_systems', text: 'Technology systems', type: 'scale',
          labels: ['Needs updating', 'Basic, needs attention', 'Adequate', 'Up-to-date, effective', 'Advanced or proprietary'] },
        { id: 'supplier_relationships', text: 'Supply chain risks', type: 'scale',
          labels: ['High risk, few suppliers', 'Some risk, limited options', 'Managed, room to improve', 'Low risk, strong ties', 'No risk / not applicable'] },
        { id: 'scalability', text: 'Business scalability without owner involvement', type: 'scale',
          labels: ['Heavily owner-dependent', 'Some dependencies', 'Moderately scalable', 'Largely scalable', 'Fully scalable'] },
        { id: 'quality_control', text: 'Quality control and customer satisfaction systems', type: 'scale',
          labels: ['Informal, reactive', 'Basic systems', 'Good systems', 'Excellent systems', 'Industry-leading'] }
      ]
    },
    {
      id: 'team',
      title: 'Team Strength',
      description: 'A capable, motivated team shows the business can succeed without you at the centre',
      questions: [
        { id: 'management_team', text: 'Strength and depth of management team', type: 'scale',
          labels: ['No leaders beyond owner', 'Minimal, needs attention', 'Adequate, needs alignment', 'Good, leaders emerging', 'Strong senior team'] },
        { id: 'key_person_risk', text: 'Key person dependencies', type: 'scale',
          labels: ['Extreme dependency', 'High dependency', 'Moderate dependency', 'Low dependency', 'No dependencies'] },
        { id: 'succession_planning', text: 'Succession planning and knowledge transfer', type: 'scale',
          labels: ['No plan in place', 'Basic, needs work', 'Plan needs alignment', 'Team aligned, in progress', 'Clear plan, well aligned'] },
        { id: 'employee_retention', text: 'Employee retention and engagement', type: 'scale',
          labels: ['High turnover, low morale', 'Some turnover, needs work', 'Average retention', 'Low turnover, good morale', 'Stable, motivated team'] }
      ]
    },
    {
      id: 'documentation',
      title: 'Business Records In Order',
      description: "Buyers will expect clear, organised records to check what you've told them. Good paperwork builds trust and speeds up the deal",
      questions: [
        { id: 'financial_records', text: 'Financial record keeping and organization', type: 'scale',
          labels: ['Incomplete, hard to follow', 'Poor, disorganised', 'Adequate records', 'Well-kept, up-to-date', 'Comprehensive, audit-ready'] },
        { id: 'operational_documentation', text: 'Operational manuals and documentation', type: 'scale',
          labels: ['Few processes written', 'Basic, limited docs', 'Adequate documentation', 'Well organised, incomplete', 'Clear manuals and procedures'] },
        { id: 'legal_documentation', text: 'Legal documents and contract organization', type: 'scale',
          labels: ['Missing key documents', 'Disorganised', 'Partially organised', 'Well organised, incomplete', 'Complete, perfectly organised'] },
        { id: 'data_room_readiness', text: 'All docs ready for buyer inspection (data room readiness)', type: 'scale',
          labels: ['Not ready', 'Early stages', 'Partially ready', 'Mostly ready', 'Completely ready'] }
      ]
    },
    {
      id: 'legal',
      title: 'Legal Foundations',
      description: 'Strong contracts, protection of ideas, and compliance reduce deal risk and protect your value',
      questions: [
        { id: 'legal_structure', text: 'Company setup and decision-making', type: 'scale',
          labels: ['Creates problems', 'Basic compliance only', 'Compliant but inefficient', 'Well structured, not current', 'Clear, efficient, well defined'] },
        { id: 'contracts', text: 'Essential contracts in place', type: 'scale',
          labels: ['Missing key contracts', 'Disorganised, gaps', 'Most contracts in place', 'All in hand, some disorder', 'All up-to-date, watertight'] },
        { id: 'intellectual_property', text: 'Protecting your ideas and brand', type: 'scale',
          labels: ['No protection in place', 'Some, not prioritised', 'Adequate protection', 'Good, not comprehensive', 'Strong IP strategy'] },
        { id: 'regulatory_compliance', text: 'Meeting industry rules', type: 'scale',
          labels: ['At risk of penalties', 'Unsure of requirements', 'Generally compliant', 'Fully compliant', 'Compliant and ahead'] }
      ]
    },
    {
      id: 'contact_info',
      title: 'Contact Information',
      description: 'Almost done! Please provide your full contact information below and then click on "Complete Assessment". Your exit readiness score and personal recommendations will follow on the next page.',
      questions: [
        { id: 'first_name', text: 'Your First Name', type: 'text', required: true },
        { id: 'family_name', text: 'Your Family Name', type: 'text', required: true },
        { id: 'job_title', text: 'Your Job Title', type: 'text', required: true },
        { id: 'email', text: 'Email Address', type: 'email', required: true },
        { id: 'phone', text: 'Telephone Number', type: 'tel', required: true },
        { id: 'company_address', text: 'Company Head Office Address', type: 'textarea', required: true },
        { id: 'email_consent', text: 'I agree to receive follow-up emails from Exit Strategy & Solutions regarding my assessment and relevant exit planning information', type: 'checkbox', required: false },
        { id: 'mail_consent', text: 'I agree to receive marketing materials by post from Exit Strategy & Solutions', type: 'checkbox', required: false }
      ]
    }
  ];

  const verifyEmail = useCallback(async (email) => {
    if (!email || email.trim() === '') {
      setEmailValidation({ status: 'idle', message: '', suggestion: null });
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setEmailValidation({ status: 'error', message: 'Please enter a valid email address', suggestion: null });
      return;
    }
    setEmailCheckLoading(true);
    setEmailValidation({ status: 'checking', message: 'Verifying email...', suggestion: null });
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const res = await fetch(`${apiUrl}/api/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (data.valid) {
        setEmailValidation({ status: 'valid', message: data.reason, suggestion: null });
      } else {
        setEmailValidation({ status: 'error', message: data.reason, suggestion: data.suggestion || null });
      }
    } catch {
      setEmailValidation({ status: 'valid', message: 'Email format is valid', suggestion: null });
    } finally {
      setEmailCheckLoading(false);
    }
  }, []);

  const applySuggestion = useCallback((suggestion) => {
    const sectionId = sections[currentSection].id;
    setResponses(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], email: suggestion }
    }));
    setEmailValidation({ status: 'idle', message: '', suggestion: null });
    verifyEmail(suggestion);
  }, [currentSection, sections, verifyEmail]);

  const handleResponse = (questionId, value, type = 'scale') => {
    const sectionId = sections[currentSection].id;
    setResponses(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [questionId]: type === 'scale' ? parseInt(value) : value
      }
    }));
  };

  const nextSection = () => {
    const currentQuestions = sections[currentSection].questions;
    const currentResponses = responses[sections[currentSection].id] || {};
    const unansweredQuestions = currentQuestions.filter(q => {
      const value = currentResponses[q.id];
      if (q.type === 'scale') return !value || (typeof value !== 'number') || value < 1 || value > 5;
      if (['text', 'email', 'tel', 'textarea', 'select'].includes(q.type)) {
        if (q.required) return !value || value === '';
        return false;
      }
      if (q.type === 'checkbox') {
        if (q.required) return !value;
        return false;
      }
      return false;
    });
    if (unansweredQuestions.length > 0) {
      const questionTitles = unansweredQuestions.map(q => q.text).join('\n\u2022 ');
      alert(`Please answer all questions before continuing:\n\n\u2022 ${questionTitles}`);
      return;
    }
    if (sections[currentSection].id === 'contact_info') {
      const emailConsent = currentResponses['email_consent'];
      const mailConsent = currentResponses['mail_consent'];
      if (!emailConsent && !mailConsent) {
        alert('Please provide consent for either email or postal marketing communications to view your results.');
        return;
      }
      if (emailValidation.status === 'error') {
        alert('Please enter a valid email address before continuing. ' + emailValidation.message);
        return;
      }
      if (emailValidation.status === 'checking' || emailCheckLoading) {
        alert('Please wait while we verify your email address.');
        return;
      }
      if (emailValidation.status === 'idle' && currentResponses['email']) {
        alert('Please click outside the email field to verify your email address before continuing.');
        return;
      }
    }
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      submitAssessment();
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submitAssessment = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${apiUrl}/api/assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(responses),
      });
      if (!response.ok) throw new Error('Assessment submission failed');
      const assessmentResult = await response.json();
      setResult(assessmentResult);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('There was an error processing your assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetAssessment = () => {
    setCurrentSection(0);
    setResponses({ financial: {}, operational: {}, legal: {}, market: {}, team: {}, documentation: {}, business_info: {}, contact_info: {} });
    setResult(null);
    setEmailValidation({ status: 'idle', message: '', suggestion: null });
    setEmailCheckLoading(false);
  };

  const currentSectionData = sections[currentSection];
  const hasScaleQuestions = currentSectionData.questions.some(q => q.type === 'scale');

  // ── HEADER COMPONENT ──
  const Header = () => (
    <div className="bg-[#2d4059] sticky top-0 z-50 shadow-lg" data-testid="brand-header">
      <div className="max-w-[860px] mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center gap-3 md:gap-5">
        <img src="https://s3.ca-central-1.amazonaws.com/logojoy/logos/206276554/noBgColor.png" alt="Exit Strategy & Solutions" className="h-[48px] md:h-[72px] flex-shrink-0" />
        <div className="w-px h-8 md:h-12 bg-white/25 flex-shrink-0"></div>
        <span className="text-[#ffd460] font-semibold text-[13px] md:text-[16.5px] leading-tight">Exit Readiness and Deal Support for UK Business Owners</span>
      </div>
    </div>
  );

  // ── STEP TRACKER ──
  const StepTracker = () => (
    <div className="flex items-center justify-between mb-10 w-full" data-testid="step-tracker">
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={i}>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
              i < currentSection ? 'bg-[#ffd460] text-[#2d4059]' :
              i === currentSection ? 'bg-[#2d4059] text-[#ffd460]' :
              'bg-gray-200 text-gray-400'
            }`}>{i + 1}</div>
            <span className={`hidden md:inline text-[10px] font-semibold tracking-wider uppercase whitespace-nowrap ${
              i < currentSection ? 'text-[#374151]' :
              i === currentSection ? 'text-[#2d4059]' :
              'text-gray-400'
            }`}>{label}</span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-0.5 md:mx-1 min-w-[8px] md:min-w-[12px] ${i < currentSection ? 'bg-[#ffd460]' : 'bg-gray-200'}`}></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ── SCALE GUIDE ──
  const [guideOpen, setGuideOpen] = useState(false);

  const ScaleGuideContent = () => (
    <>
      {[
        { n: 1, title: 'Big risk', desc: 'Major gaps. Needs urgent attention.', color: 'bg-red-50 text-red-800' },
        { n: 2, title: 'Weak spot', desc: 'Basic foundations but fragile.', color: 'bg-orange-50 text-orange-700' },
        { n: 3, title: 'Adequate', desc: 'Works for today, not buyer-ready.', color: 'bg-amber-50 text-amber-700' },
        { n: 4, title: 'Strong', desc: 'Well-managed. Minor tweaks needed.', color: 'bg-blue-50 text-blue-700' },
        { n: 5, title: 'Buyer-ready', desc: 'Best practice, low risk.', color: 'bg-green-50 text-green-700' },
      ].map((item, i) => (
        <div key={i} className={`flex items-start gap-2.5 ${i < 4 ? 'mb-3.5 pb-3.5 border-b border-gray-100' : ''}`}>
          <div className={`w-[26px] h-[26px] rounded-md flex items-center justify-center text-[13px] font-bold flex-shrink-0 ${item.color}`}>{item.n}</div>
          <div className="text-[13px] text-[#6B7280] leading-snug">
            <strong className="text-[#374151] block">{item.title}</strong>{item.desc}
          </div>
        </div>
      ))}
    </>
  );

  // Desktop sticky sidebar
  const ScaleGuideSidebar = () => (
    <div className="hidden md:block scale-guide-glass sticky top-[130px] rounded-xl p-5" data-testid="scale-guide">
      <h4 className="text-xs tracking-widest uppercase font-semibold text-[#2d4059] mb-4 text-center">Rating Guide</h4>
      <ScaleGuideContent />
    </div>
  );

  // Mobile collapsible banner
  const ScaleGuideMobile = () => (
    <div className="md:hidden mb-4" data-testid="scale-guide-mobile">
      <button
        onClick={() => setGuideOpen(!guideOpen)}
        className="w-full flex items-center justify-between bg-white rounded-xl px-5 py-3.5 shadow-sm border border-gray-100 text-sm font-semibold text-[#2d4059]"
        data-testid="scale-guide-toggle"
      >
        <span>Rating Guide (1-5)</span>
        <span className="text-[#ffd460] text-lg">{guideOpen ? '\u25B2' : '\u25BC'}</span>
      </button>
      {guideOpen && (
        <div className="bg-white rounded-b-xl px-5 py-4 shadow-sm border border-t-0 border-gray-100 page-enter">
          <ScaleGuideContent />
        </div>
      )}
    </div>
  );

  // ── BENTO CARD (replaces radio buttons) ──
  const BentoCard = ({ value, label, selected, onClick, questionId }) => (
    <div
      data-testid={`bento-card-${questionId}-${value}`}
      onClick={onClick}
      className={`bento-card ${selected ? 'selected' : ''}`}
    >
      <div className={`bento-num text-[22px] md:text-[22px] font-bold mb-1.5 md:mb-1.5 ${selected ? 'text-[#2d4059]' : 'text-gray-400'}`}>{value}</div>
      <div className={`bento-label text-[15px] md:text-[13px] leading-snug ${selected ? 'text-[#374151] font-medium' : 'text-[#6B7280]'}`}>{label}</div>
      {selected && <div className="bento-dot w-1.5 h-1.5 rounded-full bg-[#ffd460] mx-auto md:mx-auto mt-2 md:mt-2"></div>}
    </div>
  );

  // ══════════════════════════════════════════════
  // RESULTS PAGE
  // ══════════════════════════════════════════════
  if (result) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Header />
        <div className="max-w-[960px] mx-auto px-4 md:px-6 py-6 md:py-10 page-enter">
          {/* Hero Score */}
          <div className="bg-[#2d4059] rounded-2xl p-8 md:p-12 mb-8 flex flex-col md:flex-row items-center gap-6 md:gap-10 relative overflow-hidden" data-testid="results-hero">
            <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#ffd460]/[0.06]"></div>
            <div className="absolute right-20 -bottom-10 w-48 h-48 rounded-full bg-[#ffd460]/[0.04]"></div>
            <div className="text-center flex-shrink-0 relative z-10">
              <div><span className="text-[72px] md:text-[96px] font-bold text-[#ffd460] leading-none tracking-tight" data-testid="results-overall-score">{result.overall_score}</span><span className="text-3xl md:text-4xl text-[#ffd460] font-light align-super">%</span></div>
              <div className="text-base text-white/70 mt-2">
                {result.overall_score >= 80 ? 'Sale-ready' :
                 result.overall_score >= 65 ? 'Nearly ready' :
                 result.overall_score >= 50 ? 'Developing' :
                 'Early stage'}
              </div>
            </div>
            <div className="relative z-10">
              <h1 className="text-[28px] font-semibold text-white tracking-tight mb-2">Your Exit Readiness Score</h1>
              <p className="text-[15px] text-white/65 leading-relaxed">Thank you for completing our Exit Readiness Calculator. Below is your comprehensive report with personalised recommendations for your business exit planning.</p>
            </div>
          </div>

          {/* Section Scores Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="section-scores-grid">
            {Object.entries(result.section_scores).map(([section, score]) => (
              <div key={section} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-[11px] tracking-widest uppercase font-semibold text-[#6B7280] mb-3 capitalize">{section}</div>
                <div className="text-[32px] font-bold text-[#2d4059] tracking-tight mb-1">{score}%</div>
                <div className="text-[13px] font-semibold text-[#374151] mb-2">{result.section_bands?.[section] || ''}</div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-1.5 bg-[#ffd460] rounded-full progress-bar-animated" style={{ width: `${score}%` }}></div>
                </div>
              </div>
            ))}
          </div>

          {/* What This Score Means CTA */}
          <div className="bg-white rounded-xl p-8 shadow-sm border-l-4 border-[#ffd460] mb-8">
            <h3 className="text-xl font-semibold text-[#2d4059] mb-4 tracking-tight">What This Score Means</h3>
            <p className="text-base text-[#374151] leading-relaxed mb-3">Your readiness score gives you a snapshot of where you stand today. The next step is to understand <strong>what to do about it</strong>.</p>
            <p className="text-base text-[#374151] leading-relaxed mb-3">If you'd like a deeper, expert-led review — where I walk through your specific results, explain what buyers will focus on, and build a prioritised action plan — that's exactly what the Exit Readiness Assessment does.</p>
            <p className="text-base text-[#374151] leading-relaxed">It's a 3-4 session engagement, delivered over 4-6 weeks, dependent on data availability and scheduling.</p>
            <div className="mt-6">
              <a href="https://www.exitstrategyandsolutions.com/contact-us" target="_blank" rel="noopener noreferrer"
                className="inline-block bg-[#2d4059] text-white px-8 py-3.5 rounded-lg font-semibold text-base hover:bg-[#1f2d3d] transition-all shadow-sm" data-testid="consultation-cta-button">
                Book a 30-Minute Consultation
              </a>
              <span className="block text-sm text-[#6B7280] mt-2">No obligation. Just a conversation about your situation.</span>
            </div>
          </div>

          {/* Priority Action Plan */}
          <h3 className="text-xl font-semibold text-[#111827] tracking-tight mb-4">Priority Action Plan</h3>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8" data-testid="action-plan-table">
            <div className="hidden md:grid grid-cols-[120px_1fr_130px_120px] px-6 py-3 bg-gray-50 border-b border-gray-100 text-[11px] tracking-widest uppercase font-semibold text-[#6B7280]">
              <div>Section</div><div>Action</div><div>Priority</div><div>Timeline</div>
            </div>
            {result.action_plan.map((action, i) => (
              <div key={i} className="md:grid md:grid-cols-[120px_1fr_130px_120px] px-6 py-4 border-b border-gray-50 items-center">
                <div className="text-sm font-semibold text-[#2d4059] mb-1 md:mb-0">{action.section}</div>
                <div className="text-sm text-[#374151] leading-relaxed mb-1 md:mb-0">{action.action}</div>
                <div className="mb-1 md:mb-0">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                    action.priority === 'Priority action' ? 'bg-orange-50 text-orange-700' :
                    action.priority === 'Improvement action' ? 'bg-amber-50 text-amber-700' :
                    'bg-green-50 text-green-700'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      action.priority === 'Priority action' ? 'bg-orange-700' :
                      action.priority === 'Improvement action' ? 'bg-amber-700' :
                      'bg-green-700'
                    }`}></span>
                    {action.priority}
                  </span>
                </div>
                <div className="text-[13px] text-[#6B7280]">{action.timeline}</div>
              </div>
            ))}
          </div>

          {/* Key Recommendations */}
          <h3 className="text-xl font-semibold text-[#111827] tracking-tight mb-4">Key Recommendations</h3>
          <div className="bg-white rounded-xl p-7 shadow-sm mb-8">
            {result.recommendations.map((rec, i) => (
              <div key={i} className="flex gap-3 py-3 border-b border-gray-50 last:border-b-0">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ffd460] mt-2.5 flex-shrink-0"></div>
                <p className="text-base text-[#374151] leading-relaxed">{rec}</p>
              </div>
            ))}
          </div>

          {/* CTA Block */}
          <div className="bg-[#2d4059] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden mb-8" data-testid="results-cta-block">
            <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-[#ffd460]/[0.06]"></div>
            <div className="absolute -left-5 -bottom-14 w-44 h-44 rounded-full bg-[#ffd460]/[0.04]"></div>
            <h3 className="text-[26px] font-semibold text-[#ffd460] tracking-tight mb-3 relative z-10">Ready to Take the Next Step?</h3>
            <p className="text-base text-white/75 mb-7 max-w-xl mx-auto leading-relaxed relative z-10">Get professional guidance to maximise your business value and ensure a successful exit.</p>
            <div className="flex gap-4 justify-center flex-wrap relative z-10">
              <a href="https://www.exitstrategyandsolutions.com/contact-us" target="_blank" rel="noopener noreferrer"
                className="bg-[#ffd460] text-[#2d4059] px-9 py-4 rounded-lg font-semibold text-base hover:bg-[#ffe082] transition-all shadow-md" data-testid="schedule-consultation-btn">
                Schedule Free Consultation
              </a>
              <button onClick={resetAssessment}
                className="bg-transparent text-white border border-white/30 px-8 py-4 rounded-lg font-medium text-[15px] hover:border-white/50 hover:bg-white/5 transition-all" data-testid="retake-assessment-btn">
                Take Assessment Again
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white rounded-xl p-6 shadow-sm text-center">
            <p className="text-sm text-[#6B7280] font-semibold">Exit Strategy & Solutions</p>
            <p className="text-[13px] text-[#9CA3AF] mt-1">Phone: 0330 043 4689 | Email: enquiry@exitstrategyandsolutions.com</p>
            <p className="text-[13px] text-[#9CA3AF]">www.exitstrategyandsolutions.com | Kingsteignton, UK</p>
          </div>
          <p className="text-center text-xs text-[#9CA3AF] mt-4">You can unsubscribe from future communications at any time.</p>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════
  // QUESTIONNAIRE PAGES
  // ══════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      <div className="max-w-[860px] mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Title */}
        <div className="text-center mb-7 md:mb-9">
          <h1 className="text-[26px] md:text-[32px] font-semibold text-[#111827] tracking-tight mb-1.5">Exit Readiness Calculator</h1>
          <p className="text-[14px] md:text-[15px] text-[#6B7280]">Complete in approximately 10-15 minutes</p>
        </div>

        {/* Step Tracker */}
        <StepTracker />

        {/* Mobile Scale Guide (collapsible, above questions) */}
        {hasScaleQuestions && <ScaleGuideMobile />}

        {/* Content grid: questions + optional sidebar */}
        <div className={`page-enter ${hasScaleQuestions ? 'md:grid content-grid-layout md:gap-6 md:items-start' : ''}`} style={hasScaleQuestions ? {gridTemplateColumns: '1fr 230px'} : {}}>
          <div>
            {/* Question Card */}
            <div className="bg-white rounded-xl p-5 md:p-9 shadow-sm">
              <div className="text-[12px] tracking-widest uppercase font-semibold text-[#6B7280] mb-1.5">Section {currentSection + 1} of {sections.length}</div>
              <h2 className="text-[22px] md:text-[26px] font-semibold text-[#111827] tracking-tight mb-1.5">{currentSectionData.title}</h2>
              <p className="text-[15px] md:text-base text-[#6B7280] leading-relaxed mb-6 md:mb-8">{currentSectionData.description}</p>

              <div className="space-y-9">
                {currentSectionData.questions.map((question) => (
                  <div key={question.id}>
                    {question.type !== 'checkbox' && (
                      <label className="block text-[18px] font-medium text-[#111827] mb-4">{question.text}</label>
                    )}

                    {/* Scale questions → Bento cards */}
                    {question.type === 'scale' && (
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 md:gap-2.5 bento-grid-responsive">
                        {[1, 2, 3, 4, 5].map((value) => (
                          <BentoCard
                            key={value}
                            value={value}
                            label={question.labels[value - 1]}
                            selected={responses[currentSectionData.id][question.id] === value}
                            onClick={() => handleResponse(question.id, value)}
                            questionId={question.id}
                          />
                        ))}
                      </div>
                    )}

                    {question.type === 'text' && (
                      <input type="text" data-testid={`input-${question.id}`}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base text-[#374151] focus:ring-2 focus:ring-[#ffd460] focus:border-transparent outline-none transition-all bg-white"
                        onChange={(e) => handleResponse(question.id, e.target.value, 'text')}
                        value={responses[currentSectionData.id][question.id] || ''} required={question.required} />
                    )}

                    {question.type === 'email' && (
                      <div>
                        <div className="relative">
                          <input type="email" data-testid="email-input"
                            className={`w-full px-4 py-3 border rounded-lg text-base text-[#374151] focus:ring-2 focus:border-transparent outline-none transition-all bg-white pr-10 ${
                              emailValidation.status === 'valid' ? 'border-green-400 focus:ring-green-300' :
                              emailValidation.status === 'error' ? 'border-red-400 focus:ring-red-300' :
                              'border-gray-200 focus:ring-[#ffd460]'
                            }`}
                            onChange={(e) => { handleResponse(question.id, e.target.value, 'email'); if (emailValidation.status !== 'idle') setEmailValidation({ status: 'idle', message: '', suggestion: null }); }}
                            onBlur={(e) => verifyEmail(e.target.value)}
                            value={responses[currentSectionData.id][question.id] || ''} required={question.required} placeholder="e.g. name@company.co.uk" />
                          {emailCheckLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="animate-spin h-5 w-5 border-2 border-[#ffd460] border-t-transparent rounded-full"></div></div>}
                          {!emailCheckLoading && emailValidation.status === 'valid' && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-lg" data-testid="email-valid-icon">&#10003;</div>}
                          {!emailCheckLoading && emailValidation.status === 'error' && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-lg" data-testid="email-error-icon">&#10007;</div>}
                        </div>
                        {emailValidation.status === 'error' && <p className="mt-1.5 text-sm text-red-600" data-testid="email-error-message">{emailValidation.message}</p>}
                        {emailValidation.status === 'error' && emailValidation.suggestion && (
                          <button type="button" data-testid="email-suggestion-button"
                            className="mt-1 text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none p-0"
                            onClick={() => applySuggestion(emailValidation.suggestion)}>
                            Did you mean <strong>{emailValidation.suggestion}</strong>?
                          </button>
                        )}
                        {emailValidation.status === 'valid' && <p className="mt-1.5 text-sm text-green-600" data-testid="email-valid-message">{emailValidation.message}</p>}
                      </div>
                    )}

                    {question.type === 'tel' && (
                      <input type="tel" data-testid={`input-${question.id}`}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base text-[#374151] focus:ring-2 focus:ring-[#ffd460] focus:border-transparent outline-none transition-all bg-white"
                        onChange={(e) => handleResponse(question.id, e.target.value, 'tel')}
                        value={responses[currentSectionData.id][question.id] || ''} required={question.required} />
                    )}

                    {question.type === 'textarea' && (
                      <textarea data-testid={`input-${question.id}`}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base text-[#374151] focus:ring-2 focus:ring-[#ffd460] focus:border-transparent outline-none transition-all bg-white" rows="3"
                        onChange={(e) => handleResponse(question.id, e.target.value, 'textarea')}
                        value={responses[currentSectionData.id][question.id] || ''} required={question.required} placeholder="Please provide your company's head office address" />
                    )}

                    {question.type === 'checkbox' && (
                      <div className="flex items-start space-x-3">
                        <input type="checkbox" id={question.id} data-testid={`checkbox-${question.id}`}
                          className="mt-1 flex-shrink-0"
                          onChange={(e) => handleResponse(question.id, e.target.checked, 'checkbox')}
                          checked={responses[currentSectionData.id][question.id] || false} required={question.required} />
                        <label htmlFor={question.id} className="text-[15px] text-[#6B7280] leading-relaxed cursor-pointer">{question.text}</label>
                      </div>
                    )}

                    {question.type === 'select' && (
                      <select data-testid={`select-${question.id}`}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base text-[#374151] focus:ring-2 focus:ring-[#ffd460] focus:border-transparent outline-none transition-all bg-white"
                        onChange={(e) => handleResponse(question.id, e.target.value, 'select')}
                        value={responses[currentSectionData.id][question.id] || ''} required={question.required}>
                        <option value="">Please select...</option>
                        {question.options.map((option, optIndex) => (
                          <option key={optIndex} value={option}>{option}</option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-7">
              <button onClick={prevSection} disabled={currentSection === 0} data-testid="form-prev-button"
                className="bg-white text-[#374151] border border-gray-200 px-7 py-3.5 rounded-lg text-[15px] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:border-gray-300 hover:bg-gray-50 transition-all">
                Previous
              </button>
              <span className="text-sm text-[#9CA3AF]">{currentSection + 1} of {sections.length}</span>
              <button onClick={nextSection} disabled={loading} data-testid="form-next-button"
                className="bg-[#2d4059] text-white px-8 py-3.5 rounded-lg text-[15px] font-semibold hover:bg-[#1f2d3d] transition-all shadow-md disabled:opacity-50">
                {loading ? 'Processing...' : currentSection === sections.length - 1 ? 'Complete Assessment' : 'Next'}
              </button>
            </div>
          </div>

          {/* Sticky Scale Guide - desktop only (mobile version is above) */}
          {hasScaleQuestions && <ScaleGuideSidebar />}
        </div>
      </div>
    </div>
  );
};

export default App;
